/**
 * `StoryClock` — monotonic story seconds (ARCHITECTURE.md §8.1).
 *
 * Two rules make the player seekable:
 *
 * 1. `dt` is clamped to 100 ms, so a throttled tab that wakes after 9 s
 *    advances the story by 0.1 s, not 9 s. The child never comes back to a
 *    story that fast-forwarded past the picture.
 * 2. While a narration/dialogue buffer plays the clock is **audio-master**:
 *    it follows `storyAtStart + audio.currentLineTimeMs()/1000`, so karaoke
 *    word highlighting and talk clips cannot drift from the voice, however
 *    badly `requestAnimationFrame` is behaving.
 *
 * The follow is **phase-locked**, not a copy. An audio clock moves in the
 * driver's buffer steps (9-20 ms against 16.7 ms frames), and copying it onto
 * story time made every walk, blink and camera move stutter while anyone
 * spoke — about two thirds of a story. Each frame the clock predicts
 * `t + dt × rate` and pulls towards the audio by a fraction of the error
 * (`FOLLOW_GAIN`, bounded to half a frame's step). A big lead of the audio
 * (a seek, a throttled tab, a buffer that starts late) is taken in one jump;
 * a story that runs ahead of the voice waits for it. It never rewinds.
 *
 * `rate` is the grown-up's playback speed (0.5×-2×, the control bar's menu).
 * Story seconds stay story seconds — the seek bar, cues and karaoke are
 * unchanged — the clock just walks through them `rate` times faster.
 *
 * The clock holds *no* rendering state. Nothing here reads the wall clock on
 * its own — the host passes `dtMs`, which is what lets a test drive the whole
 * player with `advance(16.7)` in a loop and get the same numbers every run.
 */

/* --------------------------------------------------------- playback rate */

/** The speeds the player offers (control-bar menu, `<` / `>`, the grown-up panel). */
export const PLAYBACK_RATES: readonly number[] = [0.5, 0.75, 1, 1.25, 1.5, 2]
export const MIN_RATE = 0.5
export const MAX_RATE = 2

/** Any number → a playable rate: non-finite or non-positive is 1, the rest is clamped to 0.5-2. */
export function clampRate(rate: number): number {
  if (typeof rate !== 'number' || !Number.isFinite(rate) || rate <= 0) return 1
  return Math.min(MAX_RATE, Math.max(MIN_RATE, rate))
}

/**
 * One step slower (`-1`) or faster (`+1`) through `PLAYBACK_RATES`, from
 * wherever `rate` is (an in-between value steps to its neighbour). Clamped at
 * both ends, so holding `>` stops at 2×.
 */
export function stepRate(rate: number, delta: -1 | 1): number {
  const r = clampRate(rate)
  if (delta > 0) return PLAYBACK_RATES.find((x) => x > r + 1e-6) ?? MAX_RATE
  let prev = MIN_RATE
  for (const x of PLAYBACK_RATES) {
    if (x >= r - 1e-6) break
    prev = x
  }
  return prev
}

/** "1×", "1.5×", "0.75×". */
export function formatRate(rate: number): string {
  return `${Number(clampRate(rate).toFixed(2))}×`
}

/* ----------------------------------------------------------- the follow */

/** Audio this far ahead of the predicted story time is taken in one jump (s). */
export const FOLLOW_JUMP_S = 0.25
/** Story this far ahead of the audio waits for it instead of easing (s). */
export const FOLLOW_WAIT_S = 0.25
/** Fraction of the audio/story error corrected per frame. */
export const FOLLOW_GAIN = 0.15

/** What the audio track hands the clock while a line is playing. */
export interface AudioMaster {
  /** Milliseconds into the current line's buffer; `null` when it is not (yet) playing. */
  currentLineTimeMs(): number | null
}

export interface StoryClockOptions {
  /** Maximum story time one `advance()` may add, ms (§8.1: 100). */
  maxStepMs?: number
  /** Playback rate (0.5-2); `setRate` changes it while playing. */
  rate?: number
  /** Story seconds to start at. */
  t?: number
}

export class StoryClock {
  private _t: number
  private _paused = true
  private _rate: number
  private readonly maxStepMs: number

  /** Audio master, armed by `bindAudio()` while a line plays. */
  private master: AudioMaster | null = null
  private masterStoryT = 0
  /** Last story time the master produced, so losing the master never rewinds. */
  private masterLast = 0

  constructor(opts: StoryClockOptions = {}) {
    this.maxStepMs = opts.maxStepMs ?? 100
    this._rate = opts.rate === undefined ? 1 : clampRate(opts.rate)
    this._t = opts.t ?? 0
  }

  /** Story seconds. */
  get t(): number {
    return this._t
  }

  get paused(): boolean {
    return this._paused
  }

  get rate(): number {
    return this._rate
  }

  /** True while a line's decoded buffer is driving the clock. */
  get audioMastered(): boolean {
    return this.master !== null
  }

  /** Playback speed. Clamped to 0.5-2; nonsense is 1. */
  setRate(rate: number): void {
    this._rate = clampRate(rate)
  }

  pause(): void {
    this._paused = true
  }

  resume(): void {
    this._paused = false
  }

  /** Jump to `t` (a seek). Drops the audio master: the caller re-arms it after restarting the line. */
  reset(t: number): void {
    this._t = Number.isFinite(t) ? Math.max(0, t) : 0
    this.master = null
    this.masterLast = this._t
  }

  /**
   * Start a scene transition's pre-roll: `t` may be negative (`−lead`), and `advance` steps it up
   * through 0 like any other story second — at the rate, frozen while paused. A non-finite `t`
   * is ignored. Drops the audio master, as a seek does.
   */
  preroll(t: number): void {
    if (!Number.isFinite(t)) return
    this._t = t
    this.master = null
    this.masterLast = this._t
  }

  /**
   * Hand the clock to the audio element/buffer that is playing the line that
   * started at story time `storyAtStart`. Pass `null` when the line ends.
   */
  bindAudio(master: AudioMaster | null, storyAtStart = this._t): void {
    if (master) {
      this.master = master
      this.masterStoryT = storyAtStart
      this.masterLast = this._t
    } else {
      this.master = null
    }
  }

  /**
   * Advance by a frame. Returns the new story time.
   * `dtMs` is clamped to `maxStepMs`; negative and non-finite deltas read 0.
   */
  advance(dtMs: number): number {
    if (this._paused) return this._t
    const step = Number.isFinite(dtMs) && dtMs > 0 ? Math.min(dtMs, this.maxStepMs) : 0
    const stepS = (step / 1000) * this._rate
    if (this.master) {
      const ms = this.master.currentLineTimeMs()
      // A line time is a position inside a buffer, so it is never negative.
      // The ports say "nothing is playing" with `-1` (see `AudioPort`), and
      // `Number.isFinite(-1)` is `true` — so testing only for finiteness makes
      // the clock take -1 as gospel and pin story time to the line's start for
      // ever. That is a story that plays its first sentence and then freezes.
      if (ms !== null && Number.isFinite(ms) && ms >= 0) {
        this._t = this.follow(this.masterStoryT + ms / 1000, stepS)
        this.masterLast = this._t
        return this._t
      }
      // No buffer is mastering the clock — not started yet (decode, unlock), or
      // this line is a Web Speech / silent fallback. Fall through to the free clock.
    }
    this._t += stepS
    this.masterLast = this._t
    return this._t
  }

  /**
   * Audio-master, phase-locked (see the header). Monotonic: a buffer that
   * reports a stale time never rewinds the story.
   */
  private follow(fromAudio: number, stepS: number): number {
    const t = Math.max(this._t, this.masterLast)
    const predicted = t + stepS
    const err = fromAudio - predicted
    // Far behind the voice (a late start, a seek inside the buffer): catch up at once.
    if (err > FOLLOW_JUMP_S) return fromAudio
    // Far ahead of it (a stalled or stale buffer): wait for the voice.
    if (err < -FOLLOW_WAIT_S) return t
    const bound = 0.5 * stepS
    const nudge = Math.min(bound, Math.max(-bound, FOLLOW_GAIN * err))
    return Math.max(t, predicted + nudge)
  }
}

/**
 * A frame source. Wall clock lives here and nowhere else in the runner: the
 * host pumps `onFrame(dtMs)`. `requestAnimationFrame` in a browser, a manual
 * loop in a test.
 */
export interface FrameSource {
  start(cb: (dtMs: number) => void): void
  stop(): void
}

/** `requestAnimationFrame` frame source. Falls back to a 60 Hz timer where rAF is absent. */
export function rafFrameSource(win: Window & typeof globalThis = globalThis as never): FrameSource {
  let handle = 0
  let timer: ReturnType<typeof setInterval> | null = null
  let last = 0
  let running = false
  const hasRaf = typeof win.requestAnimationFrame === 'function'

  return {
    start(cb) {
      if (running) return
      running = true
      last = 0
      if (hasRaf) {
        const tick = (now: number): void => {
          if (!running) return
          const dt = last === 0 ? 16.7 : now - last
          last = now
          cb(dt)
          handle = win.requestAnimationFrame(tick)
        }
        handle = win.requestAnimationFrame(tick)
      } else {
        timer = setInterval(() => cb(16.7), 16)
      }
    },
    stop() {
      running = false
      if (hasRaf && handle) win.cancelAnimationFrame(handle)
      if (timer) clearInterval(timer)
      handle = 0
      timer = null
    },
  }
}
